/**
 * The Deep Sea — depth markers from the surface to the bottom of the Mariana
 * Trench.
 *
 * The figures here are real, which is the point on a platform about facts: a
 * dive that invents its own depths would be the same mistake as inventing crowd
 * percentages. Depths are the commonly cited ranges rather than single precise
 * values, because most of these creatures live across a band rather than at a
 * line. Where a number is a record rather than a typical depth, the copy says so.
 */
export type DepthMarker = {
  /** Metres below the surface. */
  depth: number;
  emoji: string;
  name: string;
  fact: string;
};

/** Named light zones, used for the banner as you pass through them. */
export const ZONES = [
  {
    from: 0,
    to: 200,
    name: 'Sunlight zone',
    note: 'Almost all ocean life lives here.'
  },
  {
    from: 200,
    to: 1000,
    name: 'Twilight zone',
    note: 'Too dark for photosynthesis.'
  },
  {
    from: 1000,
    to: 4000,
    name: 'Midnight zone',
    note: 'No sunlight reaches this far.'
  },
  {
    from: 4000,
    to: 6000,
    name: 'The abyss',
    note: 'Near freezing, and almost empty.'
  },
  {
    from: 6000,
    to: 11_034,
    name: 'The trenches',
    note: 'Deeper than most of the sea floor.'
  }
] as const;

export const MAX_DEPTH = 10_935;

export const MARKERS: DepthMarker[] = [
  {
    depth: 0,
    emoji: '⛵',
    name: 'Sea level',
    fact: 'The ocean covers about 71% of the planet and holds around 97% of its water.'
  },
  {
    depth: 4,
    emoji: '🐬',
    name: 'Bottlenose dolphin',
    fact: 'Usually surfaces every few minutes, though it can hold a dive for far longer.'
  },
  {
    depth: 10,
    emoji: '🐠',
    name: 'Coral reef',
    fact: 'Reefs cover under 1% of the ocean floor but support around a quarter of all marine species.'
  },
  {
    depth: 30,
    emoji: '🤿',
    name: 'Recreational diving limit',
    fact: 'About as deep as most recreational scuba certifications allow.'
  },
  {
    depth: 60,
    emoji: '🐙',
    name: 'Common octopus',
    fact: 'Has around 500 million neurons — most of them in its arms rather than its head.'
  },
  {
    depth: 100,
    emoji: '🦈',
    name: 'Great white shark',
    fact: 'Ranges through the upper water column and has been tracked well below 1,000 m.'
  },
  {
    depth: 200,
    emoji: '🌑',
    name: 'End of the sunlight zone',
    fact: 'Below this, there is not enough light for photosynthesis.'
  },
  {
    depth: 332,
    emoji: '🏅',
    name: 'Deepest scuba dive',
    fact: 'The record set by Ahmed Gabr in 2014 — it took him about 15 minutes down and 14 hours back up.'
  },
  {
    depth: 535,
    emoji: '🐧',
    name: 'Emperor penguin',
    fact: 'The deepest recorded dive by a bird, on a single breath.'
  },
  {
    depth: 700,
    emoji: '🦑',
    name: 'Vampire squid',
    fact: 'Lives where oxygen is too low for most animals, and eats mostly marine snow.'
  },
  {
    depth: 1000,
    emoji: '🌚',
    name: 'Start of the midnight zone',
    fact: 'No sunlight at all from here down. Most light you would see is made by animals.'
  },
  {
    depth: 1200,
    emoji: '🐟',
    name: 'Anglerfish',
    fact: 'The lure glows from bioluminescent bacteria living inside it.'
  },
  {
    depth: 1800,
    emoji: '🐋',
    name: 'Sperm whale',
    fact: 'Hunts squid on dives that can last over an hour.'
  },
  {
    depth: 2000,
    emoji: '🦐',
    name: 'Hydrothermal vents',
    fact: 'Whole ecosystems here run on chemical energy from the vents, not sunlight.'
  },
  {
    depth: 2992,
    emoji: '🐢',
    name: 'Leatherback turtle',
    fact: 'The deepest dive recorded for any reptile.'
  },
  {
    depth: 3800,
    emoji: '🚢',
    name: 'Wreck of the Titanic',
    fact: 'Resting on the sea floor since 1912, and still slowly being eaten by bacteria.'
  },
  {
    depth: 4000,
    emoji: '🧊',
    name: 'The abyss begins',
    fact: 'Water here sits a little above freezing, under roughly 400 atmospheres of pressure.'
  },
  {
    depth: 6000,
    emoji: '🕳️',
    name: 'The trenches begin',
    fact: 'Only a small fraction of the sea floor is deeper than this.'
  },
  {
    depth: 8336,
    emoji: '🐡',
    name: 'Deepest fish ever filmed',
    fact: 'A snailfish recorded in 2022 in the Izu-Ogasawara Trench off Japan.'
  },
  {
    depth: 10_935,
    emoji: '🏴',
    name: 'Challenger Deep',
    fact: 'The deepest known point in the ocean. Pressure here is over 1,000 times what you feel at the surface.'
  }
];

export const zoneAt = (depth: number) =>
  ZONES.find((zone) => depth >= zone.from && depth < zone.to) ??
  ZONES[ZONES.length - 1];

/** Background colour for a depth — surface blue fading to black. */
export const shadeAt = (depth: number) => {
  const t = Math.min(depth / 1500, 1);
  const top = { r: 14, g: 96, b: 148 };
  const bottom = { r: 0, g: 4, b: 12 };
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);

  return `rgb(${mix(top.r, bottom.r)} ${mix(top.g, bottom.g)} ${mix(top.b, bottom.b)})`;
};
